import type {
  Discipline,
  LevelAssessmentModel,
  StructuredRubricFields,
} from "@shared/labs/level-assessment-model";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { C, DM, MONO, UI } from "../results/ui-tokens";
import { BootstrapProgress } from "./bootstrap-progress";
import {
  type AxisDraft,
  type CellMap,
  CompetencyMatrix,
  emptyAxis,
  type FieldsMap,
} from "./competency-matrix";
import {
  buildAxisPayload,
  buildRubricCells,
  buildRubricFields,
  levelsArray,
  removeAxisReindex,
  removeAxisReindexFields,
  setCellIn,
} from "./matrix-cells";
import { MatrixFileImport } from "./matrix-file-import";
import { disciplineImportToDraft, parseDisciplineMatrixFile } from "./matrix-import";

/**
 * Step 1 of the **new-discipline** create→calibrate journey: define a
 * discipline's vertical competency matrix on an existing model, then flow into
 * calibration. The level ladder and custom level names are inherited from the
 * model (read-only here) — the discipline only chooses its level sub-band and
 * its vertical axes. Structurally the same procedure as the new-model matrix
 * step, scoped to one discipline. On save the discipline is created and
 * bootstrapped (scoped), and the created id is handed to the shell.
 */
export function DisciplineMatrixStep({
  modelId,
  onCreated,
  stepIndicator,
}: {
  modelId: string;
  onCreated: (info: { disciplineId: string }) => void;
  stepIndicator?: React.ReactNode;
}) {
  const { toast } = useToast();

  const [model, setModel] = useState<LevelAssessmentModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  // Optional sub-band of the model range; undefined = the full range.
  const [band, setBand] = useState<{ min: number; max: number } | undefined>(undefined);
  const [axes, setAxes] = useState<AxisDraft[]>([emptyAxis()]);
  const [cells, setCells] = useState<CellMap>({});
  const [fields, setFields] = useState<FieldsMap>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelGet({ modelId })) as {
          model: LevelAssessmentModel;
        };
        if (!cancelled) setModel(res.model);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Erro ao carregar o modelo.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const levelNames = model?.levels.names ?? {};
  const modelMin = model?.levels.min ?? 0;
  const modelMax = model?.levels.max ?? 0;

  // Effective band, clamped to the model range.
  const effBand = useMemo(() => {
    const c = (n: number) => Math.min(modelMax, Math.max(modelMin, n));
    const min = c(band?.min ?? modelMin);
    const max = c(band?.max ?? modelMax);
    return { min: Math.min(min, max), max: Math.max(min, max) };
  }, [band, modelMin, modelMax]);
  const isFullBand = effBand.min === modelMin && effBand.max === modelMax;
  const bandLevels = useMemo(() => levelsArray(effBand.min, effBand.max), [effBand]);
  const modelLevels = useMemo(() => levelsArray(modelMin, modelMax), [modelMin, modelMax]);

  const validation = useMemo(() => {
    const issues: string[] = [];
    if (label.trim().length === 0) issues.push("Nome da disciplina obrigatório.");
    if (axes.length === 0) issues.push("Pelo menos uma competência vertical.");
    for (let i = 0; i < axes.length; i++) {
      if (axes[i].label.trim().length === 0) issues.push(`Eixo #${i + 1}: nome obrigatório.`);
      if (!(axes[i].weight > 0) || !Number.isFinite(axes[i].weight)) {
        issues.push(`Eixo #${i + 1}: peso deve ser um número maior que zero.`);
      }
    }
    return { issues, valid: issues.length === 0 };
  }, [label, axes]);

  function updateAxis(idx: number, patch: Partial<AxisDraft>) {
    setAxes((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function addAxis() {
    setAxes((prev) => [...prev, emptyAxis()]);
  }
  function removeAxis(idx: number) {
    setAxes((prev) => prev.filter((_, i) => i !== idx));
    setCells((prev) => removeAxisReindex(prev, idx));
    setFields((prev) => removeAxisReindexFields(prev, idx));
  }
  function setCell(axisIdx: number, level: number, value: string) {
    setCells((prev) => setCellIn(prev, axisIdx, level, value));
  }
  function setFieldsForAxis(axisIdx: number, next: StructuredRubricFields) {
    setFields((prev) => ({ ...prev, [axisIdx]: next }));
  }

  /** Apply an uploaded discipline matrix file (JSON/YAML): replaces label,
   *  description, sub-band, and the vertical axes/cells/fields. Returns a
   *  human-readable error to display, or `null` on success. The band is
   *  re-validated ⊆ the model range on submit. */
  function applyDisciplineFile(text: string): string | null {
    const res = parseDisciplineMatrixFile(text);
    if (!res.ok) return res.error;
    const d = disciplineImportToDraft(res.data);
    setLabel(d.label);
    setDescription(d.description);
    setBand(d.band);
    setAxes(d.axes);
    setCells(d.cells);
    setFields(d.fields);
    return null;
  }

  const levelOption = (l: number) => {
    const nm = levelNames[String(l)]?.trim();
    return nm ? `L${l} · ${nm}` : `L${l}`;
  };

  async function submit() {
    if (!validation.valid || submitting || !model) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = (await rpcClient.labs.levelAssessmentDisciplineCreate({
        modelId,
        label: label.trim(),
        description: description.trim(),
        axes: axes.map(buildAxisPayload),
        rubricCells: buildRubricCells(axes, cells, bandLevels),
        rubricFields: buildRubricFields(axes, fields, bandLevels),
        // Omit when the function spans the full model range.
        ...(isFullBand ? {} : { levels: effBand }),
        bootstrap: true,
      })) as { discipline: Discipline };
      toast({
        title: "Disciplina criada e bootstrapada",
        description: `${res.discipline.label} pronta pra calibração.`,
      });
      onCreated({ disciplineId: res.discipline.id });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      setSubmitError(message);
      toast({ title: "Falha ao criar disciplina", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="ci-stripe ci-stripe-page">
        <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1200 }}>
          <div style={{ ...UI, color: C.ink50, fontSize: 14 }}>Carregando modelo…</div>
        </div>
      </div>
    );
  }
  if (loadError || !model) {
    return (
      <div className="ci-stripe ci-stripe-page">
        <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 760 }}>
          <div
            style={{
              ...UI,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              background: C.white,
              padding: "14px 18px",
              fontSize: 13.5,
              color: C.violet,
            }}
          >
            Falha ao carregar o modelo: {loadError ?? "modelo não encontrado"}
          </div>
          <div style={{ marginTop: 14 }}>
            <BackLink href={`/labs/level-assessment/models/${modelId}/edit`}>
              Voltar ao modelo
            </BackLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1200 }}>
        <Link
          href={`/labs/level-assessment/models/${modelId}/edit`}
          style={{
            ...UI,
            fontSize: 12,
            color: C.ink50,
            textDecoration: "none",
            letterSpacing: "0",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          ← Voltar ao modelo
        </Link>
        <h1
          style={{
            ...DM,
            margin: "10px 0 8px",
            fontSize: 30,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.02em",
          }}
        >
          Nova disciplina — {model.name}
        </h1>
        <p
          style={{
            ...UI,
            margin: "0 0 24px",
            fontSize: 14,
            color: C.ink70,
            lineHeight: 1.55,
            maxWidth: 760,
          }}
        >
          Defina as competências <strong style={{ color: C.ink }}>verticais</strong> desta
          disciplina, avaliadas só quando a avaliação roda contra ela. Os níveis (L{modelMin}–L
          {modelMax}) e seus nomes vêm do modelo. Você pode limitar a função a uma faixa contínua de
          níveis. Ao salvar, a disciplina é bootstrapada e seguimos direto pra calibração.
        </p>

        {stepIndicator && <div style={{ marginBottom: 24 }}>{stepIndicator}</div>}

        {/* 1. Identidade */}
        <section style={cardStyle}>
          <div style={sectionHeadStyle}>
            <SectionBadge>1</SectionBadge>
            <h2 style={sectionTitleStyle}>Identidade</h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(200px, 280px) 1fr",
              gap: 16,
              maxWidth: 760,
            }}
          >
            <Field label="Nome da disciplina">
              <TokenInput
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Engenharia"
                disabled={submitting}
                data-testid="discipline-step-label"
              />
            </Field>
            <Field label="Descrição">
              <TokenInput
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Competências técnicas verticais de engenharia."
                disabled={submitting}
                data-testid="discipline-step-description"
              />
            </Field>
          </div>
        </section>

        {/* 2. Níveis + competências verticais */}
        <section style={{ ...cardStyle, marginTop: 18 }}>
          <div style={sectionHeadStyle}>
            <SectionBadge>2</SectionBadge>
            <h2 style={sectionTitleStyle}>Competências verticais</h2>
          </div>
          <p style={subtitleStyle}>
            Avaliadas só quando a avaliação roda contra esta disciplina. Escolha um eixo e preencha
            nível a nível.
          </p>

          {/* Faixa de níveis desta função */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 18,
            }}
          >
            <span style={{ ...UI, fontSize: 12.5, fontWeight: 600, color: C.ink70 }}>
              Faixa de níveis desta função:
            </span>
            <select
              value={effBand.min}
              disabled={submitting}
              data-testid="discipline-step-level-min"
              onChange={(e) => {
                const min = Number(e.target.value);
                setBand({ min, max: Math.max(min, effBand.max) });
              }}
              style={bandSelectStyle}
            >
              {modelLevels.map((l) => (
                <option key={l} value={l}>
                  {levelOption(l)}
                </option>
              ))}
            </select>
            <span style={{ ...UI, fontSize: 12.5, color: C.ink50 }}>até</span>
            <select
              value={effBand.max}
              disabled={submitting}
              data-testid="discipline-step-level-max"
              onChange={(e) => {
                const max = Number(e.target.value);
                setBand({ min: Math.min(effBand.min, max), max });
              }}
              style={bandSelectStyle}
            >
              {modelLevels.map((l) => (
                <option key={l} value={l}>
                  {levelOption(l)}
                </option>
              ))}
            </select>
            {!isFullBand && (
              <TokenButton
                variant="ghost"
                onClick={() => setBand(undefined)}
                disabled={submitting}
                data-testid="discipline-step-level-reset"
              >
                usar range completo
              </TokenButton>
            )}
            <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>
              {isFullBand
                ? `Cobre L${modelMin}–L${modelMax} (range completo).`
                : `Cobre L${effBand.min}–L${effBand.max}.`}
            </span>
          </div>

          <MatrixFileImport
            testidPrefix="discipline"
            disabled={submitting}
            hint="Carrega a disciplina de um arquivo (label, levels opcional, axes com cells/boundaries)."
            successMessage="Disciplina importada."
            onApply={applyDisciplineFile}
          />

          <CompetencyMatrix
            axes={axes}
            cells={cells}
            fields={fields}
            levels={bandLevels}
            levelNames={levelNames}
            disabled={submitting}
            testidPrefix="discipline-step"
            onAddAxis={addAxis}
            onRemoveAxis={removeAxis}
            onUpdateAxis={updateAxis}
            onSetCell={setCell}
            onSetFields={setFieldsForAxis}
          />
        </section>

        {!validation.valid && (
          <div
            style={{
              ...UI,
              marginTop: 18,
              padding: "16px 20px",
              borderRadius: 12,
              border: `1px solid ${C.amberFg}`,
              background: C.amberBg,
            }}
            data-testid="discipline-step-issues"
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.amberFg,
                textTransform: "uppercase",
                letterSpacing: "0",
                marginBottom: 8,
              }}
            >
              Pendências
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {validation.issues.map((issue) => (
                <div key={issue} style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.4 }}>
                  {issue}
                </div>
              ))}
            </div>
          </div>
        )}

        {validation.valid && !submitError && (
          <div
            style={{
              ...UI,
              marginTop: 18,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13.5,
              fontWeight: 500,
              color: C.green,
            }}
          >
            <CheckIcon />
            Tudo certo — pronto pra bootstrapar.
          </div>
        )}

        {submitError && (
          <div
            style={{
              ...UI,
              marginTop: 18,
              padding: "14px 18px",
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.white,
              color: C.violet,
              fontSize: 13,
            }}
          >
            {submitError}
          </div>
        )}

        {/* In-flight bootstrap progress — the scoped create+bootstrap is ~20-40s
            of Opus calls; show the same per-axis progress the model flow uses
            instead of leaving the user with just a button label. */}
        {submitting && (
          <div style={{ marginTop: 18 }}>
            <BootstrapProgress
              running
              result={null}
              scopes={[
                {
                  key: "discipline",
                  label: label.trim() || "Disciplina",
                  axes: axes.map((a, i) => ({
                    id: `axis-${i}`,
                    label: a.label || `Eixo ${i + 1}`,
                  })),
                },
              ]}
            />
          </div>
        )}

        {/* sticky footer action bar */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            marginTop: 24,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderTop: `1px solid ${C.ink10}`,
            padding: "14px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <Link
            href={`/labs/level-assessment/models/${modelId}/edit`}
            style={{
              ...DM,
              fontWeight: 700,
              fontSize: 14,
              color: C.ink70,
              textDecoration: "none",
              padding: "10px 16px",
            }}
          >
            Cancelar
          </Link>
          <button
            type="button"
            onClick={submit}
            disabled={!validation.valid || submitting}
            data-testid="discipline-step-submit"
            style={{
              ...DM,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 700,
              fontSize: 14,
              padding: "12px 22px",
              borderRadius: 999,
              border: "none",
              cursor: !validation.valid || submitting ? "not-allowed" : "pointer",
              background: !validation.valid || submitting ? C.ink10 : C.red,
              color: !validation.valid || submitting ? C.ink50 : C.white,
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            {submitting ? (
              "Criando + bootstrapando…"
            ) : (
              <>
                Criar e bootstrapar{" "}
                <span style={{ ...UI, fontWeight: 500, fontSize: 12, opacity: 0.85 }}>
                  ({1 + axes.length} chamadas Opus, ~20-40s)
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Local Comp-token UI helpers
// ───────────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ ...UI, fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 7 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function SectionBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        ...DM,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        background: C.ink,
        color: C.white,
      }}
    >
      {children}
    </span>
  );
}

function BackLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      style={{
        ...DM,
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 700,
        fontSize: 13,
        padding: "9px 18px",
        borderRadius: 999,
        background: C.white,
        color: C.ink,
        border: `1px solid ${C.ink}`,
        textDecoration: "none",
      }}
    >
      {children}
    </Link>
  );
}

function TokenInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { style, disabled, ...rest } = props;
  return (
    <input
      disabled={disabled}
      style={{
        ...DM,
        width: "100%",
        boxSizing: "border-box",
        padding: "11px 14px",
        borderRadius: 10,
        border: `1px solid ${C.ink10}`,
        background: disabled ? C.ink5 : C.white,
        color: C.ink,
        fontSize: 15,
        outline: "none",
        ...style,
      }}
      {...rest}
    />
  );
}

function TokenButton({
  children,
  onClick,
  disabled = false,
  variant = "ghost",
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "ghost";
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick">) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...UI,
        fontSize: 12,
        fontWeight: 600,
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${C.ink10}`,
        background: C.white,
        color: C.ink70,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Style constants
// ───────────────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  background: C.white,
  padding: "24px 26px",
};

const sectionHeadStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 18,
};

const sectionTitleStyle: React.CSSProperties = {
  ...DM,
  margin: 0,
  fontSize: 19,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: "-0.01em",
};

const subtitleStyle: React.CSSProperties = {
  ...UI,
  margin: "0 0 16px",
  fontSize: 13,
  color: C.ink70,
  lineHeight: 1.5,
  maxWidth: 760,
};

const bandSelectStyle: React.CSSProperties = {
  ...MONO,
  height: 32,
  fontSize: 13,
  padding: "0 10px",
  borderRadius: 10,
  border: `1px solid ${C.ink10}`,
  background: C.white,
  color: C.ink,
};
